import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { JobInfoContext } from "../../../job-info-context";
import { useFetchState } from "@loschmidt/commons/fetch-state";
import { JobApi } from "../../../api";

type VinaJobDesiredRunContextType = ReturnType<typeof useProviderValue>;

const VinaJobDesiredRunContext = createContext<VinaJobDesiredRunContextType | undefined>(undefined);

export const useVinaJobDesiredRunContext = () => {
  const context = useContext(VinaJobDesiredRunContext);
  if (context === undefined) {
    throw new Error("useVinaJobDesiredRunContext must be used within a VinaJobDesiredRunContextProvider");
  }
  return context;
};

export function VinaJobDesiredRunContextProvider({children}: {
  children: ReactNode,
}) {
  const value = useProviderValue();
  return (
    <VinaJobDesiredRunContext.Provider value={value}>
      {children}
    </VinaJobDesiredRunContext.Provider>
  );
}

function useProviderValue() {

  const {id, vinaJobInfo} = useContext(JobInfoContext);
  const status = vinaJobInfo?.status;
  const allowed = vinaJobInfo?.allowed;  // allowed only if residue is in structure and Vina grid is defined

  const {fetchState, withFetchState} = useFetchState();
  const fetching = fetchState.fetching;

  const [desiredRun, setDesiredRun] = useState<boolean | null>(null);

  const unscheduled = status === 0 && !desiredRun;
  const scheduled = status === 0 && desiredRun;
  const inQueue = status === 1;
  const inProgress = status === 3;
  const failed = status === -1;
  const done = status === 5;

  const triggered = typeof status === 'number' && status !== 0;

  const fetchAndSetDesiredRun = useCallback(() => {
    JobApi.vinaGetDesiredRun({id})
      .then(setDesiredRun)
      .catch(err => console.error(err));
  }, [id]);

  useEffect(() => {
    fetchAndSetDesiredRun();
  }, [fetchAndSetDesiredRun]);

  return {
    fetching,
    withFetchState,

    allowed,

    unscheduled,
    scheduled,
    inQueue,
    inProgress,
    failed,
    done,
    triggered,

    desiredRun,
    fetchAndSetDesiredRun
  };
}

